import {
  Autocomplete,
  Button,
  CircularProgress,
  createTheme,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ThemeProvider,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState, useSetRecoilState } from "recoil";
import axios from "../../../api/axios";
import { hsCodesState, newHSCodesAnimationState } from "../../../atoms/membership";
import SpringIn from "../addressInfo/SpringIn";
import Errors from "../../../utils/Errors";

const theme = createTheme({
  components: {
    MuiSelect: {
      styleOverrides: {
        select: {
          display: "flex",
        },
      },
    },
  },
});

const CreateHSCodes = () => {
  const [newHSCodesAnimation, setNewHSCodesAnimation] = useRecoilState(
    newHSCodesAnimationState
  );
  const [hsCodes, setHsCodes] = useRecoilState(hsCodesState);

  const [hsCode, setHsCode] = useState("");
  const [description, setDescription] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [hsCodeIsNotSelected, setHsCodeIsNotSelected] = useState(false);
  const [descriptionIsNotSelected, setDescriptionIsNotSelected] = useState(false);
  const [hsCodeLengthError, setHsCodeLengthError] = useState(false);
  const [loading, setLoading] = useState(false);

  // HS Code autocomplete için state'ler
  const [hsOptions, setHsOptions] = useState([]);
  const [selectedHsOption, setSelectedHsOption] = useState(null);
  const [hsCodeSearch, setHsCodeSearch] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);

  const hsCodeRef = useRef();
  const descriptionRef = useRef();

  // HS Code arama fonksiyonları
  const searchHsCodes = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length <= 2) {
      setHsOptions([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await axios.get(`hscode-search/${searchTerm}`);
      
      let searchResults = [];
      if (response.data && !response.data.error) {
        let dataToProcess = null;
        
        if (response.data.items && Array.isArray(response.data.items)) {
          dataToProcess = response.data.items;
        } 
        else if (response.data.status === 200 && response.data.data) {
          dataToProcess = response.data.data;
        } else if (Array.isArray(response.data)) {
          dataToProcess = response.data;
        } else if (response.data.htsno) {
          dataToProcess = response.data;
        }
        
        if (dataToProcess && Array.isArray(dataToProcess) && dataToProcess.length > 0) {
          searchResults = dataToProcess
            .filter(item => item && item.htsno && item.description)
            .map(item => ({
              label: `${item.htsno} - ${item.description}`,
              value: item.htsno,
              data: item,
              isMemberCode: false
            }));
        }
      }

      setHsOptions(searchResults);
    } catch (error) {
      console.error("HS kodu arama hatası:", error);
      setHsOptions([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const debouncedSearch = useCallback((searchTerm) => {
    const timeoutId = setTimeout(() => {
      searchHsCodes(searchTerm);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchHsCodes]);

  const hsCodeRegisterHandler = (e) => {
    e?.preventDefault();

    if (hsCode.length === 0) {
      setHsCodeIsNotSelected(true);
      return;
    }

    if (hsCode.length < 8) {
      setHsCodeLengthError(true);
      return;
    }

    if (description.length === 0) {
      setDescriptionIsNotSelected(true);
      return;
    }

    setHsCodeIsNotSelected(false);
    setHsCodeLengthError(false);
    setDescriptionIsNotSelected(false);

    setLoading(true);
    
    const requestData = {
      hs_code: hsCode.replace(/\./g, ""), // Nokta işaretlerini temizle
      description: description,
    };

    if (supplierId) {
      requestData.supplier_id = supplierId;
    }

    axios
      .post("member-hs-code/add", requestData)
      .then((response) => {
        toast.success("HS Kodu Başarıyla Eklendi", { autoClose: 500 });
        setHsCodes([response.data.data, ...hsCodes]);
      })
      .catch((error) => {
        // toast.error("HS Kodu eklenirken bir hata oluştu. Backend henüz hazır değil.", { autoClose: 3000 });
      })
      .finally(() => {
        setLoading(false);
        handleClose();
      });
  };

  const handleClose = () => {
    setHsCode("");
    setDescription("");
    setSupplierId("");
    setHsCodeIsNotSelected(false);
    setHsCodeLengthError(false);
    setDescriptionIsNotSelected(false);
    setNewHSCodesAnimation(false);
    
    // Autocomplete state'lerini temizle
    setHsOptions([]);
    setSelectedHsOption(null);
    setHsCodeSearch("");
    setAutocompleteOpen(false);
  };

  useEffect(() => {
    if (hsCode.length !== 0) {
      setHsCodeIsNotSelected(false);
    }

    if (hsCode.length >= 8) {
      setHsCodeLengthError(false);
    }

    if (description.length !== 0) {
      setDescriptionIsNotSelected(false);
    }
  }, [hsCode, description]);


  // Autocomplete seçimi değiştiğinde sadece HS Code'u güncelle, description'ı otomatik doldurma
  useEffect(() => {
    if (selectedHsOption && selectedHsOption.data) {
      setHsCode(selectedHsOption.value);
      // setDescription(selectedHsOption.data.description); // Bu satırı kaldırdık - kullanıcı kendi yazsın
      setHsCodeIsNotSelected(false);
      setHsCodeLengthError(false);
      setDescriptionIsNotSelected(false);
    }
  }, [selectedHsOption]);

  return (
    <>
      {newHSCodesAnimation && (
        <ThemeProvider theme={theme}>
          <SpringIn>
            <form
              className="create-hs-codes"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--gap2x)",
                marginTop: "var(--gap2x)",
              }}
              onSubmit={hsCodeRegisterHandler}
            >
              <div
                style={{
                  display: "grid",
                  gap: "var(--gap2x)",
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px , 1fr))",
                }}
              >
                <Autocomplete
                  fullWidth
                  freeSolo
                  size="small"
                  options={hsOptions}
                  open={autocompleteOpen}
                  onOpen={() => setAutocompleteOpen(true)}
                  onClose={() => setAutocompleteOpen(false)}
                  getOptionLabel={(option) => {
                    if (typeof option === "string") return option;
                    return option?.label || option?.value || "";
                  }}
                  isOptionEqualToValue={(option, value) => {
                    return option?.value === value?.value;
                  }}
                  value={selectedHsOption}
                  onChange={(event, newValue) => {
                    setSelectedHsOption(newValue);
                    
                    // Eğer string olarak girildiyse (freeSolo)
                    if (typeof newValue === "string") {
                      const cleanValue = newValue.replace(/\s+/g, "").toUpperCase();
                      setHsCode(cleanValue);
                      setHsCodeSearch(cleanValue);
                      if (cleanValue.length >= 8) {
                        setHsCodeLengthError(false);
                        setHsCodeIsNotSelected(false);
                      }
                    }
                  }}
                  onInputChange={(event, newInputValue) => {
                    const cleanValue = newInputValue.replace(/\s+/g, "").toUpperCase();
                    setHsCode(cleanValue);
                    setHsCodeSearch(cleanValue);
                    if (cleanValue.length >= 8) {
                      setHsCodeLengthError(false);
                      setHsCodeIsNotSelected(false);
                      debouncedSearch(cleanValue);
                    } else {
                      setHsOptions([]);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      autoComplete="off"
                      label="HS Kodu (GTİP No)"
                      minLength={8}
                      ref={hsCodeRef}
                      error={hsCodeIsNotSelected || hsCodeLengthError}
                      helperText={
                        hsCodeIsNotSelected
                          ? "HS Kodu girmeniz gerekmektedir."
                          : hsCodeLengthError
                          ? "HS Kodu en az 8 karakter olmalıdır."
                          : ""
                      }
                      inputProps={{ 
                        ...params.inputProps, 
                      }}
                      autoFocus
                    />
                  )}
                  loading={searchLoading}
                  noOptionsText="Sonuç Bulunamadı"
                  loadingText="Aranıyor..."
                  filterOptions={(options) => options}
                  renderOption={(props, option) => (
                    <li {...props} key={option.value}>
                      <div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <strong>{option.data?.htsno} </strong>
                          <div style={{ whiteSpace: "nowrap" }}>
                            <span style={{ fontSize: "14px", color: "#666" }}>
                              {" "}
                              Gümrük vergisi:{" "}
                            </span>
                            <span
                              style={{
                                color: "#d32f2f",
                                fontWeight: "bold",
                                fontSize: "0.9em",
                              }}
                            >
                              {option.data?.general ||
                                option.data?.special ||
                                option.data?.other ||
                                "0"}
                            </span>
                          </div>
                        </div>
                        <span style={{ fontSize: "0.9em", color: "#666" }}>
                          {option.data?.description}
                        </span>
                      </div>
                    </li>
                  )}
                />
                <TextField
                  fullWidth
                  required
                  size="small"
                  autoComplete="off"
                  label="Ürün Açıklaması"
                  ref={descriptionRef}
                  error={descriptionIsNotSelected}
                  helperText={
                    descriptionIsNotSelected
                      ? "Ürün açıklaması girmeniz gerekmektedir."
                      : ""
                  }
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
                {/* <TextField
                  fullWidth
                  size="small"
                  autoComplete="off"
                  label="Tedarikçi ID (Opsiyonel)"
                  value={supplierId}
                  onChange={(e) => setSupplierId(e.target.value)}
                /> */}
              </div>
              <div
                style={{
                  gap: "var(--gap)",
                }}
                className="new-address-register"
              >
                {loading ? (
                  <div
                    style={{
                      width: "108px",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress
                      sx={{
                        width: "33px !important",
                        height: "33px !important",
                      }}
                    />
                  </div>
                ) : (
                  <Button 
                    sx={{ 
                      height: "35px",
                      ":disabled": { opacity: 0.3 }
                    }} 
                    type="submit" 
                    className="iyzi-button-blue"
                    disabled={hsCode.length === 0 || hsCode.length < 8 || description.length === 0 || !selectedHsOption}
                  >
                    Kaydet
                  </Button>
                )}
                <Button
                  type="button"
                  sx={{ height: "35px" }}
                  className="iyzi-button-blue"
                  onClick={handleClose}
                >
                  İptal
                </Button>
              </div>
            </form>
          </SpringIn>
        </ThemeProvider>
      )}
    </>
  );
};

export default CreateHSCodes;
